import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { format, addDays, subDays } from 'date-fns';
import { it } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { selectAuth, selectUser } from '../redux/main/authSlice';
import { selectAccountData } from '../redux/main/accountSlice';
import DynamicTitle from '../components/DynamicTitle';

export default function EmployeeJobsList() {
  // Stato per la data selezionata (default: oggi)
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Stato per gli appuntamenti
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Hook per la navigazione
  const navigate = useNavigate();
  
  // Recupero token, user e account dal Redux store
  const auth = useSelector(selectAuth);
  const token = auth?.token;
  const user = useSelector(selectUser);
  const accountData = useSelector(selectAccountData);
  
  // Funzione per formattare la data nel formato desiderato
  const formatDate = (date) => {
    return format(date, 'EEEE d MMMM yyyy', { locale: it });
  };
  
  // Funzione per spostarsi al giorno precedente
  const goToPreviousDay = () => {
    setSelectedDate(prevDate => subDays(prevDate, 1));
  };
  
  // Funzione per spostarsi al giorno successivo
  const goToNextDay = () => {
    setSelectedDate(prevDate => addDays(prevDate, 1));
  };
  
  // Funzione per formattare l'orario
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'HH:mm');
  };
  
  // Funzione per recuperare gli appuntamenti dell'utente per la data selezionata
  const fetchAppointments = async () => {
    if (!token || !user?.userId || !accountData?._id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Calcolo la data di inizio (inizio della giornata selezionata)
      const fromDate = new Date(selectedDate);
      fromDate.setHours(0, 0, 0, 0);
      
      // Calcolo la data di fine (fine della giornata selezionata)
      const toDate = new Date(selectedDate);
      toDate.setHours(23, 59, 59, 999);
      
      // Preparo i parametri di query
      const params = new URLSearchParams({
        accountId: accountData._id,
        userId: user.userId,
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString()
      });
      
      // Chiamata all'API
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/appointments/byuser?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error(`Errore nel recupero degli appuntamenti: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Ordino gli appuntamenti per orario di inizio
      const sortedAppointments = data.sort((a, b) => new Date(a.fromDate) - new Date(b.fromDate));
      setAppointments(sortedAppointments);
    } catch (err) {
      console.error('Errore durante il recupero degli appuntamenti:', err);
      setError('Impossibile caricare gli appuntamenti. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };
  
  // Effetto per recuperare gli appuntamenti quando cambia la data selezionata o quando l'utente/account è caricato
  useEffect(() => {
    fetchAppointments();
  }, [selectedDate, token, user?.userId, accountData?._id]);
  
  // Funzione per troncare il testo se troppo lungo
  const truncateText = (text, maxLength = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  
  return (
    <div className="p-4 max-w-screen-lg mx-auto">
      <DynamicTitle title="Commesse Assegnate" />
      
      {/* Intestazione */}
      <div className="text-center mb-4">
        <h1 className="text-2xl font-semibold">Commesse Assegnate</h1>
      </div>
      
      {/* Navigatore date - centrato */}
      <div className="flex justify-center mb-6">
        <div className="flex items-center bg-white rounded-lg shadow px-3 py-2 w-full max-w-md mx-auto">
          <button 
            onClick={goToPreviousDay}
            className="p-2 text-gray-600 hover:text-blue-600 focus:outline-none"
            aria-label="Giorno precedente"
          >
            <i className="fi fi-br-angle-left text-lg"></i>
          </button>
          
          <div className="flex-grow text-center mx-3 font-medium">
            {formatDate(selectedDate)}
          </div>
          
          <button 
            onClick={goToNextDay}
            className="p-2 text-gray-600 hover:text-blue-600 focus:outline-none"
            aria-label="Giorno successivo"
          >
            <i className="fi fi-br-angle-right text-lg"></i>
          </button>
        </div>
      </div>
      
      {/* Contenuto principale */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Caricamento appuntamenti...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-red-500">
            <i className="fi fi-br-exclamation-circle text-2xl mb-2"></i>
            <p>{error}</p>
          </div>
        ) : appointments.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <p className="text-gray-600">Nessun appuntamento trovato per questa data</p>
          </div>
        ) : (
          appointments.map(appointment => (
            <div 
              key={appointment._id} 
              className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow cursor-pointer" 
              onClick={() => navigate(`/employee-appointment-detail/${appointment._id}`)}
            >
              {/* Intestazione card con data e orario */}
              <div className="p-4 bg-blue-50 border-b border-blue-100">
                <div className="flex justify-between items-center">
                  <div className="font-medium">
                    {format(new Date(appointment.fromDate), 'dd/MM/yyyy')}
                  </div>
                  <div className="text-sm text-gray-700">
                    {formatTime(appointment.fromDate)} - {formatTime(appointment.toDate)}
                  </div>
                </div>
              </div>
              
              {/* Corpo della card */}
              <div className="p-4">
                {/* Titolo */}
                <h3 className="text-lg font-medium mb-2">{appointment.title || 'Appuntamento senza titolo'}</h3>
                
                {/* Dati dell'appuntamento */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm mb-3">
                  {appointment.systemAddressLabel && (
                    <div className="flex items-start">
                      <i className="fi fi-br-marker mr-2 mt-0.5 text-gray-500"></i>
                      <span>{appointment.systemAddressLabel}</span>
                    </div>
                  )}
                  
                  {appointment.jobCode && (
                    <div className="flex items-start">
                      <i className="fi fi-br-briefcase mr-2 mt-0.5 text-gray-500"></i>
                      <span>Commessa: {appointment.jobCode}</span>
                    </div>
                  )}
                  
                  {appointment.jobTypeLabel && (
                    <div className="flex items-start">
                      <i className="fi fi-br-settings mr-2 mt-0.5 text-gray-500"></i>
                      <span>Tipo: {appointment.jobTypeLabel}</span>
                    </div>
                  )}
                  
                  {appointment.assistanceRequestCode && (
                    <div className="flex items-start">
                      <i className="fi fi-br-ticket mr-2 mt-0.5 text-gray-500"></i>
                      <span>RdA: {appointment.assistanceRequestCode}</span>
                    </div>
                  )}
                  
                  {appointment.maintenanceCode && (
                    <div className="flex items-start">
                      <i className="fi fi-br-tool-box mr-2 mt-0.5 text-gray-500"></i>
                      <span>Manutenzione: {appointment.maintenanceCode}</span>
                    </div>
                  )}
                </div>
                
                {/* Note (anteprima) */}
                {appointment.notes && (
                  <div className="mt-2 text-sm text-gray-600 border-t border-gray-100 pt-2">
                    <p>{truncateText(appointment.notes)}</p>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
